'use client';

import { useRef, useEffect, useState } from 'react';
import { ProofStat } from '@/components/marketing/proof-stat';
import { ChapterLabel } from '@/components/shared/chapter-label';
import { ScrollReveal } from '@/components/shared/scroll-reveal';

interface StatTile {
  value: string;
  label: string;
  fill: number; // 0–1 for the arc gauge
}

const STATS: StatTile[] = [
  { value: '40+',   label: 'Systems shipped',             fill: 0.82 },
  { value: '$2.4M+', label: 'Recovered annually',         fill: 0.76 },
  { value: '95%',   label: 'Client retention',            fill: 0.95 },
  { value: '10×',   label: 'Faster cycles vs vendor-led', fill: 0.88 },
];

// SVG arc gauge constants
// Circle: cx=60 cy=60 r=52 → C = 2π×52 ≈ 326.73
// 270° arc (gap at bottom): TRACK = C×0.75 ≈ 245.05, GAP = C×0.25 ≈ 81.68
const CX = 60, CY = 60, R = 52;
const C = 2 * Math.PI * R;
const TRACK = C * 0.75;
const GAP   = C * 0.25;

function ArcGauge({ fill }: { fill: number }) {
  const progressRef = useRef<SVGCircleElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry?.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const arcFill = TRACK * fill;

  return (
    // rotate(-135deg) moves the arc start from 3-o'clock to 7-o'clock
    // so the gap sits at the 6-o'clock (bottom) position.
    <svg
      viewBox="0 0 120 120"
      fill="none"
      className="absolute inset-0 h-full w-full"
      style={{ transform: 'rotate(-135deg)' }}
      aria-hidden="true"
    >
      {/* Background track */}
      <circle
        cx={CX} cy={CY} r={R}
        strokeWidth={1.5}
        stroke="hsl(202 78% 53% / 0.10)"
        strokeDasharray={`${TRACK} ${GAP}`}
        strokeLinecap="round"
      />
      {/* Animated progress arc */}
      <circle
        ref={progressRef}
        cx={CX} cy={CY} r={R}
        strokeWidth={2.5}
        stroke="hsl(202 78% 60%)"
        strokeDasharray={
          active
            ? `${arcFill} ${C - arcFill}`
            : `0 ${C}`
        }
        strokeLinecap="round"
        style={{
          transition: 'stroke-dasharray 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
          filter: active ? 'drop-shadow(0 0 4px hsl(202 78% 60% / 0.5))' : 'none',
        }}
      />
    </svg>
  );
}

export function Stats() {
  return (
    <section
      aria-label="Key metrics"
      className="section-accent-top relative border-t border-(--color-border) bg-(--color-bg-elevated)"
    >
      <div className="mx-auto max-w-(--container-max) px-6 py-24 md:py-32">
        {/* Eyebrow */}
        <ScrollReveal>
          <ChapterLabel number="10" title="NUMBERS" className="mb-12" />
        </ScrollReveal>

        <div className="grid grid-cols-2 gap-8 md:gap-12 lg:grid-cols-4">
          {STATS.map(({ value, label, fill }, i) => (
            <ScrollReveal
              key={label}
              delay={i * 90}
              className="flex flex-col items-center text-center"
            >
              {/* Arc gauge wraps the number */}
              <div className="relative mb-4 flex h-[120px] w-[120px] items-center justify-center">
                <ArcGauge fill={fill} />
                {/* Number centered inside arc */}
                <div className="relative z-10 flex flex-col items-center">
                  <ProofStat
                    value={value}
                    label=""
                    valueClassName="tnum font-mono text-[clamp(22px,3.5vw,32px)] font-semibold leading-none tracking-tight bg-gradient-to-br from-(--color-accent) to-(--color-accent)/70 bg-clip-text text-transparent"
                    labelClassName="hidden"
                  />
                </div>
              </div>
              {/* Label below the arc */}
              <p className="text-[13px] leading-relaxed text-(--color-text-muted)">{label}</p>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
